import React, { useContext, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  Link,
  TextField,
  Typography,
} from "@mui/material";

import {
  BulkRemoveOrgUsersFromBounceListRequest,
  Region,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import mainStyles from "../../styles";
import RegionSelector from "./shared/region-selector";
import { bulkRemoveOrgUsersFromBounceList } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface BulkRemoveBouncesFormProps {
  listSupportWorkflows: () => void;
}

const REASON_MAX_LENGTH = 1000;

const regionLabel = (r: Region | undefined): string => {
  switch (r) {
    case Region.US_EAST_1:
      return "US";
    case Region.EU_WEST_1:
      return "EU";
    case Region.AP_SOUTHEAST_2:
      return "AU";
    case Region.UNSPECIFIED:
      return "Dev";
    default:
      return "—";
  }
};

const BulkRemoveBouncesForm = ({
  listSupportWorkflows,
}: BulkRemoveBouncesFormProps) => {
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);

  const [orgId, setOrgId] = useState<string>("");
  const [targetRegion, setTargetRegion] = useState<Region>();
  const [reason, setReason] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [successJobUrl, setSuccessJobUrl] = useState<string | null>(null);

  const trimmedOrgId = orgId.trim();
  const trimmedReason = reason.trim();
  const canSubmit =
    trimmedOrgId.length > 0 &&
    targetRegion !== undefined &&
    trimmedReason.length > 0 &&
    trimmedReason.length <= REASON_MAX_LENGTH;

  const submit = async () => {
    if (!canSubmit || targetRegion === undefined) {
      return;
    }
    setIsLoading(true);
    setSuccessJobUrl(null);
    try {
      const req = new BulkRemoveOrgUsersFromBounceListRequest({
        orgId: trimmedOrgId,
        region: targetRegion,
        reason: trimmedReason,
      });
      const resp = await bulkRemoveOrgUsersFromBounceList(
        req,
        callOptionsHeaders(DEFAULT_REGION_CODE)
      );
      setSuccessJobUrl(resp.jobUrl);
      listSupportWorkflows();
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  const InfoCard = () => (
    <Grid item xs={12}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="info-panel-content"
          id="info-panel-header"
          data-testid="info-panel-summary"
        >
          <HelpIcon color="primary" />
          <Typography
            data-testid="info-panel-title"
            style={{ paddingLeft: ".25em" }}
          >
            How to use: Bulk Remove Org Users from SendGrid Bounce List
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography
            style={{ textAlign: "left" }}
            data-testid="info-panel-description"
            component="div"
          >
            <p>
              Removes every user in the specified organization from the SendGrid
              bounce list so emails can be retried.
            </p>
            <ul>
              <li>
                <strong>Org ID</strong> — the SafetyCulture organization whose
                users should be unbounced.
              </li>
              <li>
                <strong>Region</strong> — the cell where the org lives.
              </li>
              <li>
                <strong>Reason</strong> — a short justification (required,
                stored on the job record).
              </li>
            </ul>
            <p>
              The request kicks off an asynchronous job; the returned link is
              the Temporal job URL where you can track progress.
            </p>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <TextField
            label="Organization ID"
            variant="outlined"
            size="small"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            disabled={isLoading}
            required
            inputProps={{ "data-testid": "org-id-input" }}
            sx={{ minWidth: "32ch" }}
          />
          <RegionSelector
            targetRegion={targetRegion}
            setTargetRegion={setTargetRegion}
            isLoading={isLoading}
          />
          <TextField
            label="Reason"
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            required
            error={trimmedReason.length > REASON_MAX_LENGTH}
            helperText={
              trimmedReason.length > REASON_MAX_LENGTH
                ? `Reason must be ${REASON_MAX_LENGTH} characters or fewer`
                : `${trimmedReason.length}/${REASON_MAX_LENGTH}`
            }
            inputProps={{ "data-testid": "reason-input" }}
            sx={{ minWidth: "48ch" }}
          />
          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setConfirmOpen(true)}
            loading={isLoading}
            disabled={!canSubmit}
            data-testid="execute-workflow-button"
          >
            Execute Workflow
          </LoadingButton>
        </Box>
        {successJobUrl && (
          <Box sx={{ marginTop: 2 }}>
            <Alert severity="success" data-testid="job-success-alert">
              Job kicked off.{" "}
              <Link
                href={successJobUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="job-url-link"
              >
                View job
              </Link>
            </Alert>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} style={{ paddingBottom: "20px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <InfoCard />
        </Grid>
      </Grid>

      <ConfirmDialog
        title="Confirm bulk bounce-list removal"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Run"
        onConfirm={() => {
          void submit();
        }}
      >
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Typography variant="body2">
              <strong>Org ID:</strong>{" "}
              <span data-testid="confirm-org-id">{trimmedOrgId}</span>
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">
              <strong>Region:</strong>{" "}
              <span data-testid="confirm-region">
                {regionLabel(targetRegion)}
              </span>
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">
              <strong>Reason:</strong>{" "}
              <span data-testid="confirm-reason">{trimmedReason}</span>
            </Typography>
          </Grid>
        </Grid>
      </ConfirmDialog>
    </>
  );
};

export default BulkRemoveBouncesForm;
